import React from 'react';
import { Card, CardBody, CardHeader } from '../../../../components/Atoms/Card';
import Badge from '../../../../components/Atoms/Badge';
import Button from '../../../../components/Atoms/Button';
import { useI18n } from '../../../../i18n';

const ENGINE_LABEL = { ffmpeg: 'engine.ffmpeg', opencv: 'engine.opencv' };

/**
 * Step 1 — where the extracted images land.
 *
 * Two destinations. A project model creates a new version that opens straight
 * in the labeling tool. A dataset needs no project or model at all: the images
 * are parked under a name and transferred into a model later.
 */
const VideoTargetSelector = ({
  targetMode,
  datasetName,
  datasets,
  projects,
  models,
  selectedProjectId,
  selectedModelId,
  engine,
  creatingProject,
  newProjectName,
  creatingModel,
  newModelName,
  newModelType,
  isCreatingTarget,
  onSelectProject,
  onSelectModel,
  handlers,
}) => {
  const { t } = useI18n();
  const engineKey = ENGINE_LABEL[String(engine || '').toLowerCase()];
  const isDataset = targetMode === 'dataset';

  const trimmed = (datasetName || '').trim().toLowerCase();
  const matchesExisting = Boolean(
    trimmed && (datasets || []).some((d) => String(d.name || '').toLowerCase() === trimmed)
  );

  return (
    <Card className="v2i-card">
      <CardHeader
        title={t('videoToImages.target.heading')}
        actions={
          <Badge variant={engine ? 'info' : 'neutral'} icon="fa-microchip"
            title={t('videoToImages.engine.hint')}>
            {t('videoToImages.engine.label')}:{' '}
            {engineKey ? t(`videoToImages.${engineKey}`) : t('videoToImages.engine.unknown')}
          </Badge>
        }
      />
      <CardBody>
        <div className="v2i-field-group">
          <span className="v2i-label">{t('videoToImages.target.mode')}</span>
          <div className="v2i-segmented" role="radiogroup" aria-label={t('videoToImages.target.mode')}>
            {[
              { value: 'model', labelKey: 'videoToImages.target.modeModel', icon: 'fa-cube' },
              { value: 'dataset', labelKey: 'videoToImages.target.modeDataset', icon: 'fa-database' },
            ].map((mode) => (
              <button
                key={mode.value}
                type="button"
                role="radio"
                aria-checked={targetMode === mode.value}
                className={`v2i-segmented__item ${targetMode === mode.value ? 'v2i-segmented__item--active' : ''}`}
                onClick={() => handlers.setTargetMode(mode.value)}
              >
                <i className={`fa ${mode.icon} mr-1`} aria-hidden="true"></i>
                {t(mode.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {isDataset ? (
          <div className="v2i-field-group">
            <label className="v2i-label" htmlFor="v2i-dataset-name">
              {t('videoToImages.target.datasetName')}
            </label>
            <input
              id="v2i-dataset-name"
              type="text"
              className="form-control"
              maxLength={120}
              placeholder={t('videoToImages.target.datasetPlaceholder')}
              value={datasetName}
              onChange={(event) => handlers.setDatasetName(event.target.value)}
              list="v2i-dataset-names"
            />
            {/* Existing names offered so appending to one is a choice, not a typo. */}
            <datalist id="v2i-dataset-names">
              {(datasets || []).map((dataset) => (
                <option key={dataset._id} value={dataset.name} />
              ))}
            </datalist>
            <p className="v2i-hint mb-0">{t('videoToImages.target.datasetHint')}</p>
            {matchesExisting && (
              <p className="v2i-hint v2i-hint--info mb-0">
                <i className="fa fa-info-circle mr-1" aria-hidden="true"></i>
                {t('videoToImages.target.datasetExistsHint')}
              </p>
            )}
          </div>
        ) : (
          <div className="v2i-field-group">
            <div className="row">
              <div className="col-md-6 mb-2">
                <label className="v2i-label" htmlFor="v2i-project">
                  {t('videoToImages.target.project')}
                </label>
                {creatingProject ? (
                  <div className="v2i-inline-create">
                    <input
                      id="v2i-new-project"
                      type="text"
                      className="form-control"
                      autoFocus
                      maxLength={60}
                      placeholder={t('videoToImages.target.newProjectPlaceholder')}
                      value={newProjectName}
                      disabled={isCreatingTarget}
                      onChange={(event) => handlers.setNewProjectName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') { event.preventDefault(); handlers.handleCreateProject(); }
                        if (event.key === 'Escape') handlers.setCreatingProject(false);
                      }}
                    />
                    <Button variant="primary" size="sm" disabled={isCreatingTarget}
                      onClick={handlers.handleCreateProject}>
                      {isCreatingTarget
                        ? t('videoToImages.target.creating')
                        : t('videoToImages.target.create')}
                    </Button>
                    <Button variant="ghost" size="sm" disabled={isCreatingTarget}
                      onClick={() => handlers.setCreatingProject(false)}>
                      {t('videoToImages.target.cancelCreate')}
                    </Button>
                  </div>
                ) : (
                  <div className="v2i-inline-create">
                    <select
                      id="v2i-project"
                      className="form-control"
                      value={selectedProjectId}
                      onChange={(event) => onSelectProject(event.target.value)}
                    >
                      <option value="">{t('videoToImages.target.selectProject')}</option>
                      {projects.map((project) => (
                        <option key={project._id} value={project._id}>{project.name}</option>
                      ))}
                    </select>
                    <Button variant="outline-secondary" size="sm"
                      onClick={() => handlers.setCreatingProject(true)}>
                      {t('videoToImages.target.newProject')}
                    </Button>
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-2">
                <label className="v2i-label" htmlFor="v2i-model">
                  {t('videoToImages.target.model')}
                </label>
                {creatingModel ? (
                  <>
                    <div className="v2i-inline-create">
                      <input
                        id="v2i-new-model"
                        type="text"
                        className="form-control"
                        autoFocus
                        maxLength={60}
                        placeholder={t('videoToImages.target.newModelPlaceholder')}
                        value={newModelName}
                        disabled={isCreatingTarget}
                        onChange={(event) => handlers.setNewModelName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') { event.preventDefault(); handlers.handleCreateModel(); }
                          if (event.key === 'Escape') handlers.setCreatingModel(false);
                        }}
                      />
                      <Button variant="primary" size="sm" disabled={isCreatingTarget}
                        onClick={handlers.handleCreateModel}>
                        {isCreatingTarget
                          ? t('videoToImages.target.creating')
                          : t('videoToImages.target.create')}
                      </Button>
                      <Button variant="ghost" size="sm" disabled={isCreatingTarget}
                        onClick={() => handlers.setCreatingModel(false)}>
                        {t('videoToImages.target.cancelCreate')}
                      </Button>
                    </div>
                    {/* A model's type is fixed at creation and decides which
                        studio and which collections it uses, so it is asked
                        here rather than guessed. */}
                    <div className="v2i-segmented mt-2" role="radiogroup"
                      aria-label={t('videoToImages.target.modelKind')}>
                      {[
                        { value: 'classification', labelKey: 'videoToImages.target.kindClassification' },
                        { value: 'bounding_box', labelKey: 'videoToImages.target.kindDetection' },
                      ].map((kind) => (
                        <button
                          key={kind.value}
                          type="button"
                          role="radio"
                          aria-checked={newModelType === kind.value}
                          className={`v2i-segmented__item ${newModelType === kind.value ? 'v2i-segmented__item--active' : ''}`}
                          disabled={isCreatingTarget}
                          onClick={() => handlers.setNewModelType(kind.value)}
                        >
                          {t(kind.labelKey)}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="v2i-inline-create">
                    <select
                      id="v2i-model"
                      className="form-control"
                      value={selectedModelId}
                      disabled={!selectedProjectId || !models.length}
                      onChange={(event) => onSelectModel(event.target.value)}
                    >
                      <option value="">
                        {selectedProjectId && !models.length
                          ? t('videoToImages.target.noModels')
                          : t('videoToImages.target.selectModel')}
                      </option>
                      {models.map((model) => (
                        <option key={model.model_id} value={model.model_id}>
                          {model.model_name} ({model.model_type})
                        </option>
                      ))}
                    </select>
                    <Button variant="outline-secondary" size="sm"
                      disabled={!selectedProjectId}
                      onClick={() => handlers.setCreatingModel(true)}>
                      {t('videoToImages.target.newModel')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <p className="v2i-hint mb-0">{t('videoToImages.target.hint')}</p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default VideoTargetSelector;
